import { useEffect, useState } from 'react';
import { addressedProductSlug } from '@xshop/shared';
import { bootData, getProductBySlug, getStoreProducts, StorefrontApiError, type StorefrontProduct } from '../api/client';

// The catalog one visit reads once: the shop's first page of products, which its grids read, and the one
// product the page sells. A preview holds no catalogue of its own — the demo shop is a real store on the
// server, so the same reads answer it, and nothing about our demo ships in this bundle.

/** The catalog a visit holds until the store answers: no title, no photo, no price a shopper could pay. */
const EMPTY_PRODUCT: StorefrontProduct = { id: '', title: '', slug: '', status: 'ACTIVE', variants: [], images: [] };

/** What the visit knows of the catalog, whether the address named its product, and whether it named one the shop lacks. */
export type ShopCatalog = { product: StorefrontProduct; products: readonly StorefrontProduct[]; addressed: boolean; productMissing: boolean };

// Reads the product the address names, from /products/<handle> or ?product=, or null when it names none — the shared
// rule, so the server writes this very read into the page.
const addressedSlug = (): string | null => addressedProductSlug(window.location.pathname, window.location.search);

// Reads the product the page sells: the one the address names, else the shop's first.
async function pageProduct(items: readonly StorefrontProduct[]): Promise<StorefrontProduct | undefined> {
  const slug = addressedSlug();
  return slug ? await getProductBySlug(slug) : items[0];
}

// Reads the catalog the page carries, if it carries one: the shop's products, and the product the address names or the first.
function bootedCatalog(): { products: readonly StorefrontProduct[]; product: StorefrontProduct } | null {
  const products = bootData<StorefrontProduct[]>('/products');
  if (!products) return null;
  const slug = addressedSlug();
  const product = slug ? bootData<StorefrontProduct>(`/products/by-slug/${slug}`) : products[0];
  return product && product.variants?.length ? { products, product } : null;
}

// Reads the shop's products and the page's product once — from the page's own answers when it carries them, so the
// first drawing already sells the product — and reports an address naming a product it lacks.
export function useShopCatalog(): ShopCatalog {
  const [booted] = useState(bootedCatalog);
  const [product, setProduct] = useState<StorefrontProduct>(booted?.product ?? EMPTY_PRODUCT);
  const [products, setProducts] = useState<readonly StorefrontProduct[]>(booted?.products ?? []);
  const [productMissing, setProductMissing] = useState(false);
  const [addressed] = useState(() => addressedSlug() !== null);

  useEffect(() => {
    if (booted) return;
    getStoreProducts()
      .then(async (items) => {
        setProducts(items ?? []);
        const matched = await pageProduct(items ?? []);
        if (!matched || !matched.variants || matched.variants.length === 0) return;
        setProduct(matched);
      })
      .catch((err: unknown) => {
        // A product address this shop has no product for is a page that says so, never an empty product page.
        if (err instanceof StorefrontApiError && err.status === 404) return setProductMissing(true);
        console.warn('Catalog unavailable:', err instanceof Error ? err.message : err);
      });
  }, [booted]);

  return { product, products, addressed, productMissing };
}
